import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMyShortlists,
  getParentCaseShortlists,
  respondToParentShortlist,
  respondToShortlist,
} from '@/api/services/shortlistsApi';
import type {
  GetApplicationsParams,
  ParentShortlistRespondRequest,
  TutorShortlistRespondRequest,
} from '@/types/api';

export const useMyShortlists = (params?: GetApplicationsParams) => {
  return useQuery({
    queryKey: ['myShortlists', params],
    queryFn: async () => {
      const response = await getMyShortlists(params);
      return response;
    },
    retry: 1,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
};

export const useRespondToShortlistMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      shortlistId,
      request,
    }: {
      shortlistId: string;
      request: TutorShortlistRespondRequest;
    }) => respondToShortlist(shortlistId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['myShortlists'] });
      queryClient.invalidateQueries({ queryKey: ['caseShortlist'] });
    },
    retry: 1,
  });
};

export const useParentCaseShortlists = (
  caseId: string | undefined,
  params?: GetApplicationsParams
) => {
  return useQuery({
    queryKey: ['parentCaseShortlists', caseId, params],
    queryFn: async () => {
      if (!caseId) {
        throw new Error('Case ID is required');
      }

      return getParentCaseShortlists(caseId, params);
    },
    enabled: !!caseId,
    retry: 1,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
};

export const useRespondToParentShortlistMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      shortlistId,
      request,
    }: {
      shortlistId: string;
      request: ParentShortlistRespondRequest;
    }) => respondToParentShortlist(shortlistId, request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['parentCaseShortlists', data.caseId] });
      queryClient.invalidateQueries({ queryKey: ['parentCases'] });
      queryClient.invalidateQueries({ queryKey: ['case', data.caseId] });
    },
    retry: 1,
  });
};
